import type { FeatureLike } from 'ol/Feature';
import { Circle as CircleStyle, Fill, Icon, RegularShape, Stroke, Style, Text } from 'ol/style';
import type ImageStyle from 'ol/style/Image';
import Point from 'ol/geom/Point';
import type LineString from 'ol/geom/LineString';
import type MultiLineString from 'ol/geom/MultiLineString';

/**
 * How a company layer looks (GeoNexora's style editor): the style as the server keeps it in the layer's directive
 * (MapLayerDef.Style), the colour ramps and palettes it is coloured from, the classes made from a field's values,
 * and the OpenLayers styles and legend drawn from it. One place, so that the editor's preview, the map and the
 * legend never disagree.
 */

export type StyleMode = 'simple' | 'categories' | 'graduated' | 'proportional' | 'heatmap' | 'classes';
export type Marker = 'circle' | 'square' | 'triangle' | 'star' | 'cross' | 'diamond' | 'pin';
export type Method = 'quantile' | 'equal' | 'jenks' | 'manual';
export type Dash = 'solid' | 'dash' | 'dot' | 'dash-dot';
export type Pattern = 'solid' | 'hatch' | 'cross-hatch' | 'dots';

export interface StyleClass {
  equals?: string | null;
  from?: number | null;
  to?: number | null;
  fill?: string | null;
  stroke?: string | null;
  labels?: Record<string, string>;
}

export interface LayerStyle {
  mode?: StyleMode | string | null;
  fill?: string | null;
  fillOpacity?: number | null;
  stroke?: string | null;
  strokeWidth?: number | null;
  marker?: Marker | string | null;
  size?: number | null;
  by?: string | null;
  classes?: StyleClass[];
  labelColour?: string | null;
  labelSize?: number | null;
  sld?: string | null;
  method?: Method | string | null;
  ramp?: string | null;
  reverse?: boolean | null;
  classCount?: number | null;
  otherFill?: string | null;
  sizeMin?: number | null;
  sizeMax?: number | null;
  valueMin?: number | null;
  valueMax?: number | null;
  weightBy?: string | null;
  heatRadius?: number | null;
  heatBlur?: number | null;
  cluster?: boolean | null;
  clusterDistance?: number | null;
  labelHalo?: string | null;
  labelMinZoom?: number | null;
  /** Points: the marker turned this many degrees clockwise, or by a number field's value (degrees). */
  rotation?: number | null;
  rotateBy?: string | null;
  /** Lines: the line's own width; without it a line is drawn as before (the outline width plus one, at least 2). */
  lineWidth?: number | null;
  /** Lines, and areas' outlines. */
  dash?: Dash | string | null;
  lineCap?: 'butt' | 'round' | 'square' | string | null;
  lineJoin?: 'miter' | 'round' | 'bevel' | string | null;
  /** Lines: arrows along the line, the way it was drawn. */
  arrows?: boolean | null;
  /** Lines: a wider line of this colour beneath (a road's edge), casingWidth px wider on each side. */
  casing?: string | null;
  casingWidth?: number | null;
  /** Areas. */
  pattern?: Pattern | string | null;
  outlineOnly?: boolean | null;
}

export type Geometry = 'point' | 'line' | 'area' | 'any' | null | undefined;

export const MARKERS: Marker[] = ['circle', 'square', 'triangle', 'diamond', 'star', 'cross', 'pin'];
export const METHODS: Method[] = ['quantile', 'equal', 'jenks', 'manual'];
export const DASHES: Dash[] = ['solid', 'dash', 'dot', 'dash-dot'];
export const CAPS = ['butt', 'round', 'square'] as const;
export const JOINS = ['miter', 'round', 'bevel'] as const;
export const PATTERNS: Pattern[] = ['solid', 'hatch', 'cross-hatch', 'dots'];

/**
 * The ways a layer of a geometry may be drawn - the same as the server's MapLayerDef.Style.modesFor: proportional
 * symbols size points and widen lines; a heatmap is of points, and only of a layer drawn in the browser (a company's
 * layer: `browser`); one of our layers is drawn by GeoServer.
 */
export function modesFor(geometry: Geometry, browser: boolean): StyleMode[] {
  const out: StyleMode[] = ['simple', 'categories', 'graduated'];
  if (geometry === 'point' || geometry === 'line') out.push('proportional');
  if (geometry === 'point' && browser) out.push('heatmap');
  return out;
}

/** Whether points may be clustered with their count: points drawn in the browser. */
export const clustersFor = (geometry: Geometry, browser: boolean) => geometry === 'point' && browser;

export const DEFAULT_FILL = '#2563eb';
export const DEFAULT_STROKE = '#1f2937';
export const DEFAULT_OTHER = '#9ca3af';

/* ───────────────────────── ramps and palettes ───────────────────────── */

export interface Ramp {
  name: string;
  kind: 'sequential' | 'diverging' | 'qualitative';
  /** Readable by people who see colours differently. */
  safe: boolean;
  colours: string[];
}

/** ColorBrewer (Cynthia Brewer, Penn State) nine-class schemes, viridis, and two qualitative sets. */
export const RAMPS: Ramp[] = [
  { name: 'blues', kind: 'sequential', safe: true, colours: ['#f7fbff', '#deebf7', '#c6dbef', '#9ecae1', '#6baed6', '#4292c6', '#2171b5', '#08519c', '#08306b'] },
  { name: 'greens', kind: 'sequential', safe: true, colours: ['#f7fcf5', '#e5f5e0', '#c7e9c0', '#a1d99b', '#74c476', '#41ab5d', '#238b45', '#006d2c', '#00441b'] },
  { name: 'oranges', kind: 'sequential', safe: true, colours: ['#fff5eb', '#fee6ce', '#fdd0a2', '#fdae6b', '#fd8d3c', '#f16913', '#d94801', '#a63603', '#7f2704'] },
  { name: 'purples', kind: 'sequential', safe: true, colours: ['#fcfbfd', '#efedf5', '#dadaeb', '#bcbddc', '#9e9ac8', '#807dba', '#6a51a3', '#54278f', '#3f007d'] },
  { name: 'reds', kind: 'sequential', safe: true, colours: ['#fff5f0', '#fee0d2', '#fcbba1', '#fc9272', '#fb6a4a', '#ef3b2c', '#cb181d', '#a50f15', '#67000d'] },
  { name: 'ylgnbu', kind: 'sequential', safe: true, colours: ['#ffffd9', '#edf8b1', '#c7e9b4', '#7fcdbb', '#41b6c4', '#1d91c0', '#225ea8', '#253494', '#081d58'] },
  { name: 'ylorrd', kind: 'sequential', safe: true, colours: ['#ffffcc', '#ffeda0', '#fed976', '#feb24c', '#fd8d3c', '#fc4e2a', '#e31a1c', '#bd0026', '#800026'] },
  { name: 'viridis', kind: 'sequential', safe: true, colours: ['#440154', '#472d7b', '#3b528b', '#2c728e', '#21918c', '#28ae80', '#5ec962', '#addc30', '#fde725'] },
  { name: 'rdylbu', kind: 'diverging', safe: true, colours: ['#d73027', '#f46d43', '#fdae61', '#fee090', '#ffffbf', '#e0f3f8', '#abd9e9', '#74add1', '#4575b4'] },
  { name: 'rdbu', kind: 'diverging', safe: true, colours: ['#b2182b', '#d6604d', '#f4a582', '#fddbc7', '#f7f7f7', '#d1e5f0', '#92c5de', '#4393c3', '#2166ac'] },
  { name: 'piyg', kind: 'diverging', safe: true, colours: ['#c51b7d', '#de77ae', '#f1b6da', '#fde0ef', '#f7f7f7', '#e6f5d0', '#b8e186', '#7fbc41', '#4d9221'] },
  { name: 'brbg', kind: 'diverging', safe: true, colours: ['#8c510a', '#bf812d', '#dfc27d', '#f6e8c3', '#f5f5f5', '#c7eae5', '#80cdc1', '#35978f', '#01665e'] },
  { name: 'spectral', kind: 'diverging', safe: false, colours: ['#d53e4f', '#f46d43', '#fdae61', '#fee08b', '#ffffbf', '#e6f598', '#abdda4', '#66c2a5', '#3288bd'] },
  { name: 'set2', kind: 'qualitative', safe: true, colours: ['#66c2a5', '#fc8d62', '#8da0cb', '#e78ac3', '#a6d854', '#ffd92f', '#e5c494', '#b3b3b3'] },
  { name: 'dark2', kind: 'qualitative', safe: true, colours: ['#1b9e77', '#d95f02', '#7570b3', '#e7298a', '#66a61e', '#e6ab02', '#a6761d', '#666666'] },
  { name: 'paired', kind: 'qualitative', safe: true, colours: ['#a6cee3', '#1f78b4', '#b2df8a', '#33a02c', '#fb9a99', '#e31a1c', '#fdbf6f', '#ff7f00', '#cab2d6', '#6a3d9a', '#ffff99', '#b15928'] },
  { name: 'bold', kind: 'qualitative', safe: false, colours: ['#e11d48', '#2563eb', '#16a34a', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#4d7c0f', '#0f766e', '#9333ea'] },
];

export function ramp(name?: string | null): Ramp {
  return RAMPS.find((r) => r.name === name) ?? RAMPS[0];
}

/** k colours spread evenly over a ramp (a palette is used in order, repeating). */
export function coloursOf(name: string | null | undefined, k: number, reverse?: boolean | null): string[] {
  const r = ramp(name);
  // a sequential ramp's palest colour is all but white on a light map: it starts one step darker
  const own = r.kind === 'sequential' && r.name !== 'viridis' ? r.colours.slice(1) : r.colours;
  const c = reverse ? [...own].reverse() : own;
  if (r.kind === 'qualitative') return Array.from({ length: k }, (_, i) => c[i % c.length]);
  if (k <= 1) return [c[c.length - 1]];
  return Array.from({ length: k }, (_, i) => c[Math.round((i * (c.length - 1)) / (k - 1))]);
}

/* ───────────────────────── classes ───────────────────────── */

export interface FieldValueCount {
  value: string;
  count: number;
}

export interface FieldStats {
  field: string;
  type: string;
  count: number;
  empty: number;
  distinct: number;
  values: FieldValueCount[];
  number?: {
    count: number;
    min?: number;
    max?: number;
    mean?: number;
    breaks?: Record<string, Record<string, number[]>>;
  };
}

export function isNumericType(type?: string): boolean {
  return type === 'integer' || type === 'decimal';
}

/** A category per value, the most common first, the rest in "Other". */
export function categoryClasses(values: FieldValueCount[], top: number, palette: string, lang: string): StyleClass[] {
  const shown = values.slice(0, top);
  const colours = coloursOf(palette, shown.length);
  return shown.map((v, i) => ({ equals: v.value, fill: colours[i], labels: { [lang]: v.value } }));
}

const round = (n: number) => {
  const a = Math.abs(n);
  const d = a >= 1000 ? 0 : a >= 10 ? 1 : a >= 1 ? 2 : 4;
  return Number(n.toFixed(d));
};

export function rangeLabel(from: number, to: number, locale: string): string {
  const f = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
  return `${f.format(from)} – ${f.format(to)}`;
}

/** Ranges from class edges (lowest first, highest last): each range runs up to and including its upper edge. */
export function graduatedClasses(edges: number[], rampName: string, reverse: boolean, locale: string, lang: string): StyleClass[] {
  const e = edges.map(round);
  const k = Math.max(1, e.length - 1);
  const colours = coloursOf(rampName, k, reverse);
  const out: StyleClass[] = [];
  for (let i = 0; i < k; i++) {
    const from = e[i];
    const to = e[Math.min(i + 1, e.length - 1)];
    out.push({ from, to, fill: colours[i], labels: { [lang]: rangeLabel(from, to, locale) } });
  }
  return out;
}

/** Edges for a method from the server's statistics, or spread evenly between the range for manual. */
export function edgesFor(stats: FieldStats | undefined, method: string, k: number): number[] {
  const n = stats?.number;
  if (!n || n.min === undefined || n.max === undefined) return [];
  const b = n.breaks?.[method === 'manual' ? 'equal' : method]?.[String(k)];
  if (b && b.length >= 2) return b;
  return Array.from({ length: k + 1 }, (_, i) => n.min! + ((n.max! - n.min!) * i) / k);
}

export function classLabel(c: StyleClass, lang: string): string {
  const own = c.labels?.[lang] ?? c.labels?.en ?? Object.values(c.labels ?? {})[0];
  if (own) return own;
  if (c.equals !== undefined && c.equals !== null) return c.equals;
  if (c.from !== undefined && c.from !== null && c.to !== undefined && c.to !== null) return `${c.from} – ${c.to}`;
  return '';
}

/** The class a value falls in: by value for categories, by range (upper edge included) for numbers; -1 for none. */
export function classIndex(style: LayerStyle, value: unknown): number {
  const classes = style.classes ?? [];
  if (value === null || value === undefined || value === '') return -1;
  const ranged = classes.some((c) => c.from !== undefined && c.from !== null);
  if (!ranged) {
    const v = String(value);
    return classes.findIndex((c) => c.equals === v);
  }
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(n)) return -1;
  for (let i = 0; i < classes.length; i++) {
    const c = classes[i];
    const lo = c.from ?? -Infinity;
    const hi = c.to ?? Infinity;
    if ((i === 0 ? n >= lo : n > lo) && n <= hi) return i;
  }
  // values added after the classes were made: below the lowest is the first class, above the highest the last
  if (classes.length > 0 && n < (classes[0].from ?? -Infinity)) return 0;
  if (classes.length > 0 && n > (classes[classes.length - 1].to ?? Infinity)) return classes.length - 1;
  return -1;
}

/* ───────────────────────── zooms and scales ───────────────────────── */

/** The map's scale (1:scale) at a zoom level, as web maps reckon it (0.28 mm pixels). */
const SCALE_AT_ZERO = 559082264.028;
export const zoomToScale = (z: number) => SCALE_AT_ZERO / Math.pow(2, z);
export const scaleToZoom = (s: number) => Math.round(Math.log2(SCALE_AT_ZERO / s) * 10) / 10;

/* ───────────────────────── OpenLayers styles ───────────────────────── */

function rgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** A line's width as drawn: its own; before lines had one, the outline width plus one, at least 2 (Sld.java the same). */
export function lineWidthOf(s: LayerStyle): number {
  if (s.lineWidth != null) return s.lineWidth;
  return Math.max(2, (s.strokeWidth ?? 1) + 1);
}

/** A dash pattern for a line of a width; undefined for solid. The same as the server's (Sld.dashArray). */
export function dashArray(dash: string | null | undefined, width: number): number[] | undefined {
  const w = Math.max(1, width);
  switch (dash) {
    case 'dash': return [Math.max(4, w * 3), Math.max(3, w * 2)];
    case 'dot': return [Math.max(1, w), Math.max(3, w * 2)];
    case 'dash-dot': return [Math.max(4, w * 3), Math.max(3, w * 2), Math.max(1, w), Math.max(3, w * 2)];
    default: return undefined;
  }
}

/** A map pin: a drop with a hole, its tip at the point. */
function pinSvg(fill: string, stroke: string, strokeWidth: number): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="32" viewBox="0 0 24 32">`
    + `<path d="M12 31 C12 31 2 18.5 2 11 A10 10 0 0 1 22 11 C22 18.5 12 31 12 31 Z" fill="${fill}" stroke="${stroke}" stroke-width="${Math.max(0.5, strokeWidth)}"/>`
    + `<circle cx="12" cy="11" r="3.6" fill="#ffffff" fill-opacity="0.9"/></svg>`;
}

const pins = new Map<string, Icon>();

/**
 * A marker of a shape and size (px across), turned `rotation` radians clockwise. Always drawn, even where labels are
 * decluttered: labels give way to it ('obstacle'), it does not give way to them.
 */
export function markerImage(marker: string | null | undefined, size: number, fill: string, stroke: string, strokeWidth: number,
  rotation = 0): ImageStyle {
  const radius = Math.max(2, size / 2);
  const f = new Fill({ color: fill });
  const s = strokeWidth > 0 ? new Stroke({ color: stroke, width: strokeWidth }) : undefined;
  switch (marker) {
    case 'square':
      return new RegularShape({ points: 4, radius: radius * 1.2, angle: Math.PI / 4, rotation, fill: f, stroke: s, declutterMode: 'obstacle' });
    case 'diamond':
      return new RegularShape({ points: 4, radius: radius * 1.3, angle: 0, rotation, fill: f, stroke: s, declutterMode: 'obstacle' });
    case 'triangle':
      return new RegularShape({ points: 3, radius: radius * 1.3, angle: 0, rotation, fill: f, stroke: s, declutterMode: 'obstacle' });
    case 'star':
      return new RegularShape({ points: 5, radius: radius * 1.4, radius2: radius * 0.6, angle: 0, rotation, fill: f, stroke: s, declutterMode: 'obstacle' });
    case 'cross':
      return new RegularShape({ points: 4, radius: radius * 1.3, radius2: 0, angle: Math.PI / 4, rotation, fill: f, stroke: new Stroke({ color: fill, width: Math.max(2, strokeWidth + 1) }), declutterMode: 'obstacle' });
    case 'pin': {
      const key = `${fill}|${stroke}|${strokeWidth}|${size}|${rotation}`;
      let icon = pins.get(key);
      if (!icon) {
        icon = new Icon({
          src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(pinSvg(fill, stroke, strokeWidth))}`,
          anchor: [0.5, 1],
          scale: Math.max(0.4, (size * 1.6) / 32),
          rotation,
          rotateWithView: false,
          declutterMode: 'obstacle',
        });
        if (pins.size > 500) pins.clear();
        pins.set(key, icon);
      }
      return icon;
    }
    default:
      return new CircleStyle({ radius, fill: f, stroke: s, declutterMode: 'obstacle' });
  }
}

const patterns = new Map<string, CanvasPattern | string>();

/**
 * An area's fill in a pattern of its colour: hatched, cross-hatched or dotted on an 8 px tile (GeoServer's
 * shape://slash, shape://times and shape://dot, which a platform layer's SLD uses); a plain colour for solid.
 */
export function patternFill(pattern: string | null | undefined, colour: string, opacity: number): CanvasPattern | string {
  if (!pattern || pattern === 'solid' || typeof document === 'undefined') return rgba(colour, opacity);
  const key = `${pattern}|${colour}|${opacity}`;
  const known = patterns.get(key);
  if (known) return known;
  const ratio = typeof window !== 'undefined' ? Math.max(1, Math.round(window.devicePixelRatio || 1)) : 1;
  const size = 8 * ratio;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return rgba(colour, opacity);
  const ink = rgba(colour, Math.min(1, opacity + 0.3));
  ctx.strokeStyle = ink;
  ctx.fillStyle = ink;
  ctx.lineWidth = ratio;
  const diagonal = (from: number, to: number) => {
    // drawn three times so the tile joins its neighbours seamlessly
    for (const o of [-size, 0, size]) {
      ctx.beginPath();
      ctx.moveTo(o + from, size);
      ctx.lineTo(o + to, 0);
      ctx.stroke();
    }
  };
  if (pattern === 'hatch' || pattern === 'cross-hatch') diagonal(0, size);
  if (pattern === 'cross-hatch') diagonal(size, 0);
  if (pattern === 'dots') {
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, 1.3 * ratio, 0, Math.PI * 2);
    ctx.fill();
  }
  const made = ctx.createPattern(canvas, 'repeat') ?? rgba(colour, opacity);
  if (patterns.size > 300) patterns.clear();
  patterns.set(key, made);
  return made;
}

export interface Drawn {
  style: LayerStyle;
  geometry: Geometry;
  /** The feature property a field's values are under: the file's column name where it differs from the key. */
  column: (key: string) => string;
  /** The field written beside each feature, by key. */
  label?: string | null;
}

/** What a feature is drawn as: its own geometry's kind, for a layer of mixed features. */
function kindOf(geometry: Geometry, feature: FeatureLike): 'point' | 'line' | 'area' {
  if (geometry === 'point' || geometry === 'line' || geometry === 'area') return geometry;
  const t = feature.getGeometry?.()?.getType?.() ?? '';
  if (t.includes('Point')) return 'point';
  if (t.includes('LineString')) return 'line';
  return 'area';
}

/** Arrows along a line in its direction, about every `spacing` map units, at most 60 per feature. */
function arrowsAlong(feature: FeatureLike, spacing: number, colour: string, width: number): Style[] {
  const g = feature.getGeometry?.() as LineString | MultiLineString | undefined;
  if (!g) return [];
  const lines: number[][][] = g.getType() === 'MultiLineString' ? (g as MultiLineString).getCoordinates() : g.getType() === 'LineString' ? [(g as LineString).getCoordinates()] : [];
  const out: Style[] = [];
  const size = Math.max(5, width * 2.2);
  for (const line of lines) {
    let walked = spacing / 2;
    for (let i = 1; i < line.length && out.length < 60; i++) {
      const [x1, y1] = line[i - 1];
      const [x2, y2] = line[i];
      const len = Math.hypot(x2 - x1, y2 - y1);
      while (walked <= len && out.length < 60) {
        const t = len === 0 ? 0 : walked / len;
        out.push(new Style({
          geometry: new Point([x1 + (x2 - x1) * t, y1 + (y2 - y1) * t]),
          image: new RegularShape({
            points: 3, radius: size, angle: Math.PI / 2, rotation: -Math.atan2(y2 - y1, x2 - x1),
            fill: new Fill({ color: colour }), stroke: new Stroke({ color: 'rgba(255,255,255,0.85)', width: 1 }),
          }),
        }));
        walked += spacing;
      }
      walked -= len;
    }
  }
  return out;
}

/** A look for every feature of a layer, made once per class (and label text). */
export function styleFunction(d: Drawn): (feature: FeatureLike, resolution: number) => Style | Style[] {
  const s = d.style;
  const mode = (s.mode ?? 'simple') as string;
  const fill = s.fill ?? DEFAULT_FILL;
  const stroke = s.stroke ?? DEFAULT_STROKE;
  const pointOpacity = s.fillOpacity ?? 0.9;
  const areaOpacity = s.fillOpacity ?? (d.geometry === 'point' ? 0.9 : 0.55);
  // a line styled before lines had their own width was drawn without transparency: it keeps that
  const lineOpacity = s.lineWidth != null ? (s.fillOpacity ?? 1) : 1;
  const strokeWidth = s.strokeWidth ?? 1;
  const width = lineWidthOf(s);
  const size = s.size ?? 8;
  const byColumn = s.by ? d.column(s.by) : null;
  const rotateColumn = s.rotateBy ? d.column(s.rotateBy) : null;
  const labelColumn = d.label ? d.column(d.label) : null;
  const labelMinRes = s.labelMinZoom != null ? zoomToResolution(s.labelMinZoom) : Infinity;
  const cache = new Map<string, Style[]>();

  /** The look of one class (colour) at one size (points) or width (lines), at one turn (points). */
  const look = (kind: 'point' | 'line' | 'area', key: string, colour: string, px: number, turn: number): Style[] => {
    const k = `${kind}|${key}|${px}|${turn}`;
    let st = cache.get(k);
    if (st) return st;
    if (kind === 'point') {
      st = [new Style({ image: markerImage(s.marker, px, rgba(colour, Math.min(1, pointOpacity)), stroke, strokeWidth, turn) })];
    } else if (kind === 'line') {
      const cap = (s.lineCap ?? 'butt') as CanvasLineCap;
      const join = (s.lineJoin ?? 'round') as CanvasLineJoin;
      st = [];
      if (s.casing && mode !== 'proportional') {
        st.push(new Style({ stroke: new Stroke({ color: s.casing, width: px + 2 * (s.casingWidth ?? 1), lineCap: cap, lineJoin: join }), zIndex: 0 }));
      }
      st.push(new Style({
        stroke: new Stroke({ color: rgba(colour, lineOpacity), width: px, lineCap: cap, lineJoin: join, lineDash: dashArray(s.dash, px) }),
        zIndex: 1,
      }));
    } else {
      st = [new Style({
        fill: s.outlineOnly ? undefined : new Fill({ color: patternFill(s.pattern, colour, areaOpacity) }),
        stroke: strokeWidth > 0 ? new Stroke({ color: stroke, width: strokeWidth, lineDash: dashArray(s.dash, strokeWidth) }) : undefined,
      })];
    }
    cache.set(k, st);
    return st;
  };

  const sizeOf = (v: unknown): number => {
    const n = typeof v === 'number' ? v : Number(String(v ?? '').replace(',', '.'));
    const lo = s.valueMin ?? 0;
    const hi = s.valueMax ?? (lo + 1);
    const a = s.sizeMin ?? 4;
    const b = s.sizeMax ?? 30;
    if (!Number.isFinite(n)) return a;
    const t = hi === lo ? 1 : Math.max(0, Math.min(1, (n - lo) / (hi - lo)));
    // area, not radius, grows with the value (Flannery's advice for proportional symbols, simplified)
    return Math.round(a + (b - a) * Math.sqrt(t));
  };

  const turnOf = (feature: FeatureLike): number => {
    const deg = rotateColumn ? Number(String(feature.get(rotateColumn) ?? '').replace(',', '.')) : (s.rotation ?? 0);
    return Number.isFinite(deg) ? Math.round(deg) * Math.PI / 180 : 0;
  };

  return (feature, resolution) => {
    const kind = kindOf(d.geometry, feature);
    const px = kind === 'line' ? width : size;
    const turn = kind === 'point' ? turnOf(feature) : 0;
    let base: Style[] | null;
    let colour = fill;
    if ((mode === 'categories' || mode === 'graduated' || mode === 'classes') && byColumn) {
      const i = classIndex(s, feature.get(byColumn));
      if (i >= 0) {
        colour = s.classes![i].fill ?? fill;
        base = look(kind, `c${i}`, colour, px, turn);
      } else if (mode === 'categories' && s.otherFill) {
        colour = s.otherFill;
        base = look(kind, 'other', colour, px, turn);
      } else if (mode === 'categories') {
        base = null;
      } else {
        colour = DEFAULT_OTHER;
        base = look(kind, 'none', colour, px, turn);
      }
    } else if (mode === 'proportional' && byColumn && kind !== 'area') {
      const sized = sizeOf(feature.get(byColumn));
      base = look(kind, 'p', fill, sized, turn);
    } else {
      base = look(kind, 'simple', fill, px, turn);
    }
    if (!base) return [];
    let out = base;
    if (kind === 'line' && s.arrows) {
      out = [...base, ...arrowsAlong(feature, 140 * resolution, colour, width)];
    }
    if (!labelColumn || resolution > labelMinRes) return out;
    const text = feature.get(labelColumn);
    if (text === null || text === undefined || text === '') return out;
    return [...out, new Style({
      text: new Text({
        text: String(text),
        font: `${s.labelSize ?? 12}px sans-serif`,
        fill: new Fill({ color: s.labelColour ?? '#111827' }),
        stroke: new Stroke({ color: s.labelHalo ?? '#ffffff', width: 3 }),
        offsetY: kind === 'point' ? (s.marker === 'pin' ? -Math.max(14, size * 1.7) : -Math.max(10, size)) : 0,
        placement: kind === 'line' ? 'line' : 'point',
        overflow: true,
      }),
      zIndex: 2,
    })];
  };
}

/** The map resolution (metres per pixel, Web Mercator) at a zoom. */
export function zoomToResolution(z: number): number {
  return 156543.03392804097 / Math.pow(2, z);
}

/* ───────────────────────── legend ───────────────────────── */

/** How a legend entry's symbol is drawn (components/Layers/StyleSwatch.tsx). */
export interface Symbol {
  fill: string;
  stroke: string;
  geometry: Geometry;
  /** Points: px across; lines: the width. */
  size?: number;
  marker?: string | null;
  rotation?: number | null;
  strokeWidth?: number;
  opacity?: number;
  dash?: string | null;
  casing?: string | null;
  arrows?: boolean | null;
  pattern?: string | null;
  outlineOnly?: boolean | null;
}

export type LegendItem =
  | ({ kind: 'swatch'; label: string } & Symbol)
  | { kind: 'ramp'; colours: string[]; low: string; high: string };

const HEAT = ['#0000ff', '#00ffff', '#00ff00', '#ffff00', '#ff0000'];

/** A symbol of a style in a colour (a class's, or the style's own). */
export function symbolOf(s: LayerStyle, geometry: Geometry, fill: string, size?: number): Symbol {
  const line = geometry === 'line';
  return {
    fill,
    stroke: s.stroke ?? DEFAULT_STROKE,
    geometry,
    size: size ?? (line ? lineWidthOf(s) : s.size ?? 10),
    marker: s.marker,
    rotation: s.rotateBy ? null : s.rotation,
    strokeWidth: s.strokeWidth ?? 1,
    opacity: line ? (s.lineWidth != null ? (s.fillOpacity ?? 1) : 1) : (s.fillOpacity ?? (geometry === 'point' ? 0.9 : 0.55)),
    dash: s.dash,
    casing: s.casing,
    arrows: s.arrows,
    pattern: s.pattern,
    outlineOnly: s.outlineOnly,
  };
}

export function legendItems(style: LayerStyle | null | undefined, geometry: Geometry, lang: string, otherLabel: string, title: string): LegendItem[] {
  const s = style ?? {};
  const mode = (s.mode ?? 'simple') as string;
  if (mode === 'heatmap') return [{ kind: 'ramp', colours: HEAT, low: '', high: '' }];
  if ((mode === 'categories' || mode === 'graduated' || mode === 'classes') && (s.classes?.length ?? 0) > 0) {
    const items: LegendItem[] = (s.classes ?? []).map((c) => ({
      kind: 'swatch', label: classLabel(c, lang), ...symbolOf(s, geometry, c.fill ?? s.fill ?? DEFAULT_FILL),
    }));
    if (mode === 'categories' && s.otherFill) items.push({ kind: 'swatch', label: otherLabel, ...symbolOf(s, geometry, s.otherFill) });
    return items;
  }
  if (mode === 'proportional') {
    const f = new Intl.NumberFormat(lang, { maximumFractionDigits: 2 });
    const lo = s.valueMin ?? 0;
    const hi = s.valueMax ?? 0;
    const mid = (lo + hi) / 2;
    const px = (v: number) => {
      const t = hi === lo ? 1 : Math.max(0, Math.min(1, (v - lo) / (hi - lo)));
      return Math.round((s.sizeMin ?? 4) + ((s.sizeMax ?? 30) - (s.sizeMin ?? 4)) * Math.sqrt(t));
    };
    const g: Geometry = geometry === 'line' ? 'line' : 'point';
    return [hi, mid, lo].map((v) => ({ kind: 'swatch', label: f.format(v), ...symbolOf(s, g, s.fill ?? DEFAULT_FILL, px(v)) }));
  }
  return [{ kind: 'swatch', label: title, ...symbolOf(s, geometry, s.fill ?? DEFAULT_FILL) }];
}

/** A first style for a field, as smart mapping does: ranges for a number, categories for anything else. */
export function suggestedMode(type: string | undefined, distinct: number): StyleMode {
  if (isNumericType(type) && distinct > 12) return 'graduated';
  return 'categories';
}
